import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Alert, Row, Col, Table, Button } from "react-bootstrap";
import { setOrderServiceInfo, setOrderDeliveryInfo } from "state/orderEdition/actions";
import useServiceLineSync from "app/orderTool/hooks/useServiceLineSync";
import StaffMealSection from "app/orderTool/pages/multiOrders/StaffMealSection";
import ServiceTenderPriceAlert from "app/orderTool/pages/multiOrders/ServiceTenderPriceAlert";
import { calculateWaiterCount } from "helper/serviceCalculation";

const defaultServiceInfo = {
  masterCount: 1,
  waiterCount: 0,
};

const isTruthy = (value) => value === true || value === 1 || value === "1";

const formatEur = (cents) =>
  ((cents || 0) / 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Éditeur des lignes service réellement présentes dans le panier (mode
 * classique ou mode brique). Chaque ligne expose qty + prix unitaire
 * éditables + bouton supprimer.
 *
 * Tous les dispatches passent par les helpers `useServiceLineSync` (1
 * dispatch par event admin, jamais via useEffect). Pas de cascade possible.
 */
const ServiceLinesEditor = ({
  order,
  serviceList,
  serviceInfo,
  briqMode,
  serviceBlockIndex,
  updateServiceLine,
  removeServiceLine,
  onCountChange,
}) => {
  const masterProduct = (serviceList || []).find((p) => p.role === "master");
  const waiterProduct = (serviceList || []).find((p) => p.role === "waiter");

  const items = briqMode
    ? (order?.briqueDevis?.[serviceBlockIndex]?.items || [])
    : (order?.items || []);

  const lines = items
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => {
      if (masterProduct && it.product_id === masterProduct.id) return true;
      if (waiterProduct && it.product_id === waiterProduct.id) return true;
      return false;
    });

  if (lines.length === 0) return null;

  const lineQty = (it) => Number(it.quantity ?? 0);
  const lineUnitPrice = (it) => Number(it.unit_price ?? it.price ?? 0);
  const lineRole = (it) => {
    if (masterProduct && it.product_id === masterProduct.id) return "master";
    if (waiterProduct && it.product_id === waiterProduct.id) return "waiter";
    return null;
  };
  const lineName = (it) => it.effective_name || it.name || "—";

  const handleDelete = (sourceIdx, role) => {
    removeServiceLine(sourceIdx);
    // Décrémente le count pour rester cohérent (admin a supprimé 1 ligne).
    if (role === "master") {
      const next = Math.max(0, parseInt(serviceInfo.masterCount, 10) - 1);
      onCountChange({ masterCount: next });
    } else if (role === "waiter") {
      const next = Math.max(0, parseInt(serviceInfo.waiterCount, 10) - 1);
      onCountChange({ waiterCount: next });
    }
  };

  const total = lines.reduce((sum, { it }) => sum + lineQty(it) * lineUnitPrice(it), 0);

  return (
    <div style={{ marginTop: "15px" }}>
      <h6 style={{ marginBottom: "8px" }}>Articles service</h6>
      <Table size="sm" bordered hover style={{ marginBottom: "5px" }}>
        <thead>
          <tr>
            <th>Article</th>
            <th className="text-end" style={{ width: "110px" }}>Heures</th>
            <th className="text-end" style={{ width: "130px" }}>Prix unit. (€)</th>
            <th className="text-end" style={{ width: "120px" }}>Sous-total</th>
            <th style={{ width: "60px" }} />
          </tr>
        </thead>
        <tbody>
          {lines.map(({ it, idx }) => {
            const qty = lineQty(it);
            const price = lineUnitPrice(it);
            const role = lineRole(it);
            return (
              <tr key={`${role}-${idx}`}>
                <td>{lineName(it)}</td>
                <td>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    step="0.25"
                    value={qty}
                    onChange={(e) => {
                      const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                      updateServiceLine(idx, { quantity: Number.isFinite(v) ? v : 0 });
                    }}
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    step="0.01"
                    value={(price / 100).toFixed(2)}
                    onChange={(e) => {
                      const eur = e.target.value === "" ? 0 : parseFloat(e.target.value);
                      const cents = Math.round((Number.isFinite(eur) ? eur : 0) * 100);
                      const patch = briqMode ? { unit_price: cents } : { price: cents };
                      updateServiceLine(idx, patch);
                    }}
                  />
                </td>
                <td className="text-end">{formatEur(qty * price)} €</td>
                <td className="text-center">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(idx, role)}
                    title="Supprimer cette ligne"
                  >
                    ×
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} className="text-end"><strong>Total Service</strong></td>
            <td className="text-end"><strong>{formatEur(total)} €</strong></td>
            <td />
          </tr>
        </tfoot>
      </Table>
      <small className="text-muted">
        Qty initiale = durée événement + 3h (2h MEP + 1h rangement). Éditable par ligne.
      </small>
    </div>
  );
};

const ServiceInfoComponent = ({ orderIndex }) => {
  const dispatch = useDispatch();
  const order = useSelector((state) => state.orderEdition.orders[orderIndex]);
  const serviceState = useSelector((state) => state.prestationService);

  // Hook : init counts à la coche + recalc waiterCount sur person change.
  // Expose les helpers pour manipuler les items (createServiceLines, etc.).
  const {
    briqMode,
    serviceBlockIndex,
    removeAllServiceLines,
    rebuildServiceLines,
    isServiceCalcOutOfSync,
    findServiceOptionConflicts,
    updateServiceLine,
    removeServiceLine,
    getServiceTenderPriceGaps,
    applyTenderPricesToServiceLines,
  } = useServiceLineSync(orderIndex);

  const calendarId = process.env.REACT_APP_GOOGLE_CALENDAR_ID ||
    (process.env.NODE_ENV === 'production'
      ? 'c_4f1bfbf0ca73ce205720a179678d9a0ffd646d2f910af4e8edab78f647de738f@group.calendar.google.com'
      : 'c_38e39882dfd0581db0872bda543ef51586f3def40ece66a4df552e0ff1921d6b@group.calendar.google.com');

  // serviceInfo stabilisé via useMemo (sinon nouvelle ref à chaque render →
  // useEffect ci-dessous re-trigger en boucle).
  const serviceInfo = useMemo(() => ({
    ...defaultServiceInfo,
    ...(order?.serviceInfo || {}),
  }), [order?.serviceInfo]);

  const deliveryInfo = order?.deliveryInfo || {};
  const serviceEnabled = isTruthy(deliveryInfo?.service);
  const personValue = deliveryInfo?.person;

  const [labDeparture, setLabDeparture] = useState(serviceInfo?.labDeparture || "");
  const [labArrival, setLabArrival] = useState(serviceInfo?.labArrival || "");
  const [labReturn, setLabReturn] = useState(serviceInfo?.labReturn || "");

  useEffect(() => {
    setLabDeparture(serviceInfo?.labDeparture || "");
    setLabArrival(serviceInfo?.labArrival || "");
    setLabReturn(serviceInfo?.labReturn || "");
  }, [serviceInfo]);

  // Plus de sync auto sur change de counts ou d'horaires : la mise à jour
  // des lignes service se fait uniquement (a) à la coche initiale du toggle
  // (cf. updateServiceFlag) et (b) sur clic explicite du bouton rouge
  // "Actualiser le calcul" qui apparaît quand `isServiceCalcOutOfSync`
  // retourne true (paramètres ont changé sans re-sync).

  const calendarLink = useMemo(() => {
    const eventId = order?.googleCalendarEventId;
    if (!eventId) return null;
    if (calendarId) {
      try {
        const payload = `${eventId} ${calendarId}`;
        const encoded = window.btoa(unescape(encodeURIComponent(payload)));
        return `https://calendar.google.com/calendar/u/0/r/eventedit/${encoded}`;
      } catch (error) {
        console.error("Impossible de construire le lien Google Calendar", error);
      }
    }
    return `https://calendar.google.com/calendar/u/0/r/eventedit/${encodeURIComponent(eventId)}`;
  }, [order?.googleCalendarEventId, calendarId]);

  const handleUpdate = useCallback(
    (patch) => {
      dispatch(setOrderServiceInfo(orderIndex, { ...serviceInfo, ...patch }));
    },
    [dispatch, orderIndex, serviceInfo]
  );

  /**
   * Coche / décoche "Service nécessaire".
   *
   * Coche : dispatch service=1 + init counts + crée les lignes service
   *   directement via `rebuildServiceLines` (plus de sync auto via useEffect).
   *   Les changements ultérieurs de paramètres (horaires, counts, person)
   *   ne re-syncent PAS automatiquement — le bouton rouge "Actualiser le
   *   calcul" apparaît à la place et c'est le user qui décide.
   * Décoche : dispatch service=0 + supprime toutes les lignes + reset counts.
   */
  const updateServiceFlag = useCallback(
    (enabled) => {
      dispatch(setOrderDeliveryInfo(orderIndex, { ...deliveryInfo, service: enabled ? 1 : 0 }));
      if (enabled) {
        const m = parseInt(serviceInfo.masterCount, 10) > 0 ? parseInt(serviceInfo.masterCount, 10) : 1;
        const w = parseInt(serviceInfo.waiterCount, 10) > 0
          ? parseInt(serviceInfo.waiterCount, 10)
          : calculateWaiterCount(personValue);
        dispatch(setOrderServiceInfo(orderIndex, { ...serviceInfo, masterCount: m, waiterCount: w }));
        // Création immédiate des lignes (au lieu de passer par un useEffect).
        rebuildServiceLines(m, w);
      } else {
        removeAllServiceLines();
        dispatch(setOrderServiceInfo(orderIndex, { ...serviceInfo, masterCount: 1, waiterCount: 0 }));
      }
    },
    [dispatch, orderIndex, deliveryInfo, serviceInfo, personValue, removeAllServiceLines, rebuildServiceLines]
  );

  const handleToggle = (event) => updateServiceFlag(event.target.checked);

  /**
   * Change d'un compteur (masterCount ou waiterCount) : dispatch
   * serviceInfo update uniquement. Le useEffect "sync items" se chargera
   * d'aligner les items au prochain render.
   */
  const handleNumberChange = (field, minValue = 0) => (event) => {
    const raw = event.target.value;
    if (raw === "") {
      handleUpdate({ [field]: "" });
      return;
    }
    const parsed = parseInt(raw, 10);
    const safeValue = Number.isNaN(parsed) ? minValue : Math.max(minValue, parsed);
    handleUpdate({ [field]: safeValue });
  };

  const handleLabTimeChange = (field, value) => {
    switch (field) {
      case "labDeparture": setLabDeparture(value); break;
      case "labArrival": setLabArrival(value); break;
      case "labReturn": setLabReturn(value); break;
      default: return;
    }
    handleUpdate({ [field]: value || "" });
  };

  if (orderIndex === null || orderIndex === undefined || !order) return null;

  // En mode brique avec une brique porteuse de l'option Service, on cache cette
  // section globale — toute l'UI Service (toggle + counts + tableau + bouton
  // Actualiser) est rendue *à l'intérieur* de cette brique par
  // `BriqueServiceSection` (cf. BriqueDevisComponent). Évite la duplication
  // et regroupe la config service au même endroit visuellement.
  if (briqMode && serviceBlockIndex !== -1) return null;

  // Validation cross-brique : >1 brique du devis porte `has_service_option=true`.
  // Bloque la coche du toggle Service tant que ce n'est pas résolu.
  const serviceConflicts = findServiceOptionConflicts();
  const hasServiceConflict = serviceConflicts.length > 0;

  // Détection désync : les lignes service en cart ne reflètent plus la formule
  // (counts × horaires actuels). Déclenche l'affichage du bouton rouge.
  const masterCount = parseInt(serviceInfo.masterCount, 10) || 0;
  const waiterCount = parseInt(serviceInfo.waiterCount, 10) || 0;
  // Nb de serveurs suggéré par la formule (1er palier de 50 = maître seul).
  const suggestedWaiterCount = calculateWaiterCount(personValue);
  // Désync : lignes ≠ compteurs OU nb de serveurs ≠ formule (person a changé)
  // → recalcul via le bouton, plus en temps réel.
  const serviceOutOfSync = serviceEnabled && !hasServiceConflict
    && (isServiceCalcOutOfSync(masterCount, waiterCount) || waiterCount !== suggestedWaiterCount);

  // Écarts entre le prix des lignes service et le tarif de l'AO sélectionné
  // (cas typique : l'AO a été choisi après la création des lignes).
  const tenderPriceGaps = serviceEnabled ? getServiceTenderPriceGaps() : [];

  return (
    <div>
      <h5>Service</h5>
      {calendarLink && (
        <Alert variant="info" style={{ marginBottom: "10px" }}>
          Événement Google Calendar :{" "}
          <a href={calendarLink} target="_blank" rel="noopener noreferrer">ouvrir dans un nouvel onglet</a>
        </Alert>
      )}
      {hasServiceConflict && (
        <Alert variant="danger" style={{ marginBottom: "10px" }}>
          <strong>⚠️ Plusieurs briques portent l'option Service</strong>
          <div style={{ marginTop: "4px", fontSize: "0.9em" }}>
            Une seule brique par devis peut porter cette option. Désactive
            l'option "Brique porteuse de l'option Service" sur l'une des
            briques suivantes en BO Sonata : <strong>{serviceConflicts.join(", ")}</strong>.
          </div>
        </Alert>
      )}
      <Form.Check
        type="switch"
        id={`service-needed-${orderIndex}`}
        label="Service nécessaire"
        checked={serviceEnabled}
        onChange={handleToggle}
        disabled={hasServiceConflict}
      />
      {serviceEnabled && serviceOutOfSync && (
        <Alert variant="danger" style={{ marginTop: "8px", marginBottom: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
            <span>
              <strong>Calcul service obsolète</strong> — les paramètres (heures
              ou nombre de serveurs) ont changé. Les lignes service en cart ne
              correspondent plus à la formule.
            </span>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                // Recalcule le nb de serveurs depuis la formule + reconstruit les lignes.
                const w = calculateWaiterCount(personValue);
                dispatch(setOrderServiceInfo(orderIndex, { ...serviceInfo, waiterCount: w }));
                rebuildServiceLines(masterCount, w);
              }}
            >
              Actualiser le calcul
            </Button>
          </div>
        </Alert>
      )}
      {serviceEnabled && (
        <ServiceTenderPriceAlert gaps={tenderPriceGaps} onApply={applyTenderPricesToServiceLines} />
      )}
      {serviceEnabled ? (
        <>
          <Row>
            <Col md={6}>
              <Form.Group controlId={`service-master-${orderIndex}`}>
                <Form.Label>Nombre de maître(s) d'hôtel</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={serviceInfo.masterCount === "" ? "" : serviceInfo.masterCount}
                  onChange={handleNumberChange("masterCount", 1)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId={`service-waiters-${orderIndex}`}>
                <Form.Label>Nombre de serveurs</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={serviceInfo.waiterCount === "" ? "" : serviceInfo.waiterCount}
                  onChange={handleNumberChange("waiterCount", 0)}
                />
              </Form.Group>
            </Col>
          </Row>

          <ServiceLinesEditor
            order={order}
            serviceList={serviceState.list}
            serviceInfo={serviceInfo}
            briqMode={briqMode}
            serviceBlockIndex={serviceBlockIndex}
            updateServiceLine={updateServiceLine}
            removeServiceLine={removeServiceLine}
            onCountChange={handleUpdate}
          />

          <hr style={{ marginTop: "15px", marginBottom: "15px" }} />
          <Alert variant="info" style={{ fontSize: "0.9em", marginBottom: "15px" }}>
            <strong>Note :</strong> Les champs "Arrivée labo" et "Retour labo" définissent l'horaire de l'événement dans l'agenda de prestation de service.
          </Alert>
          <Row>
            <Col md={6}>
              <Form.Group controlId={`lab-arrival-${orderIndex}`}>
                <Form.Label>
                  Arrivée labo <small className="text-muted">(définit l'horaire de début de l'événement)</small>
                </Form.Label>
                <Form.Control
                  type="time"
                  value={labArrival}
                  onChange={(e) => handleLabTimeChange("labArrival", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId={`lab-departure-${orderIndex}`}>
                <Form.Label>Départ du labo</Form.Label>
                <Form.Control
                  type="time"
                  value={labDeparture}
                  onChange={(e) => handleLabTimeChange("labDeparture", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group controlId={`lab-return-${orderIndex}`}>
                <Form.Label>
                  Fin de rangement labo <small className="text-muted">(définit l'horaire de fin de l'événement)</small>
                </Form.Label>
                <Form.Control
                  type="time"
                  value={labReturn}
                  onChange={(e) => handleLabTimeChange("labReturn", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <StaffMealSection orderIndex={orderIndex} />
        </>
      ) : (
        <Alert variant="light" style={{ marginTop: "10px" }}>
          Activez « Service » pour renseigner les effectifs.
        </Alert>
      )}
    </div>
  );
};

export default ServiceInfoComponent;
